'use client';
import { useEffect, useState } from 'react';
import { useParams } from 'next/navigation';
import Link from 'next/link';
import Header from '@/components/common/Header';
import Footer from '@/components/common/Footer';
import SectionHeader from '@/components/ui/SectionHeader';
import Accordion from '@/components/ui/Accordion';
import Button from '@/components/ui/Button';
import Badge from '@/components/ui/Badge';
import Icon from '@/components/ui/AppIcon';

import { getServiceSchema } from '@/lib/jsonld';
import { getBreadcrumbSchema } from '@/lib/jsonld';
import { getServiceBySlug, getRelatedServices } from '@/data/services';

export default function ServiceDetailPage() {
  const params = useParams();
  const slug = params?.slug as string;
  const service = getServiceBySlug(slug);
  const relatedServices = getRelatedServices(slug);

  const [isSticky, setIsSticky] = useState(false);
  const [activeSection, setActiveSection] = useState('');

  useEffect(() => {
    const reveals = document.querySelectorAll('.reveal');
    if (!reveals.length) return;
    const observer = new IntersectionObserver(
      (entries) => entries.forEach((e) => e.isIntersecting && e.target.classList.add('active')),
      { threshold: 0.1 }
    );
    reveals.forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      setIsSticky(window.scrollY > 600);

      // Section highlighting
      const sections = ['overview', 'who-this-is-for', 'whats-included', 'time-capacity', 'modules', 'example-sprint', 'proof', 'faqs'];
      for (const sectionId of sections) {
        const element = document.getElementById(sectionId);
        if (element) {
          const rect = element.getBoundingClientRect();
          if (rect.top <= 150 && rect.bottom >= 150) {
            setActiveSection(sectionId);
            break;
          }
        }
      }
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  if (!service) {
    return (
      <>
        <Header />
        <main className="min-h-screen bg-background pt-32 pb-20">
          <div className="max-w-[1200px] mx-auto px-6">
            <h1 className="text-h1 mb-6 text-white">Service Not Found</h1>
            <p className="text-gray-300 mb-8">The service you're looking for doesn't exist.</p>
            <Link href="/services/menu">
              <Button variant="primary">View All Services</Button>
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const capacityPercentage = service.capacityFit === 'light' ? 25 : service.capacityFit === 'standard' ? 50 : 75;

  return (
    <>
      <Header />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(getServiceSchema({ name: service.name, description: service.subhead, slug: service.slug, category: service.category })) }}
        suppressHydrationWarning
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(getBreadcrumbSchema([
          { name: 'Home', url: 'https://bestoddscorp.com' },
          { name: 'Services', url: 'https://bestoddscorp.com/services' },
          { name: service.category, url: 'https://bestoddscorp.com/services/menu' },
          { name: service.name, url: 'https://bestoddscorp.com/services/' + service.slug },
        ])) }}
        suppressHydrationWarning
      />

      {/* Sticky CTA Bar */}
      <div
        className={`fixed top-[73px] left-0 right-0 z-[49] bg-background/95 backdrop-blur-md border-b border-border transition-all duration-500 ${
          isSticky ? 'translate-y-0 opacity-100' : '-translate-y-full opacity-0'
        }`}
      >
        <div className="max-w-[1600px] mx-auto flex items-center justify-between gap-4 py-4 px-6">
          <div>
            <p className="text-h5">{service.name}</p>
            <p className="text-micro text-gray-400 uppercase tracking-widest">{service.typicalHours} typical</p>
          </div>
          <div className="flex items-center gap-3">
            <Link href="/book">
              <Button variant="primary" size="md" className="border-glow">
                Book Consultation
              </Button>
            </Link>
            <Link href="/pricing">
              <Button variant="secondary" size="md">
                View Pricing
              </Button>
            </Link>
          </div>
        </div>
      </div>

      {/* Mobile Sticky CTA */}
      <div
        className={`fixed bottom-0 left-0 right-0 z-40 bg-background/95 backdrop-blur-md border-t border-border transition-all duration-500 md:hidden ${
          isSticky ? 'translate-y-0 opacity-100' : 'translate-y-full opacity-0'
        }`}
      >
        <div className="flex items-center gap-3 p-4">
          <Link href="/book" className="flex-1">
            <Button variant="primary" size="md" className="w-full border-glow">
              Book Consultation
            </Button>
          </Link>
          <Link href="/pricing">
            <Button variant="secondary" size="md">
              Pricing
            </Button>
          </Link>
        </div>
      </div>

      <main className="min-h-screen bg-background pt-32 pb-20">
        {/* Breadcrumb */}
        <div className="max-w-[1200px] mx-auto px-6 mb-8">
          <div className="flex items-center gap-2 text-body-sm text-gray-400">
            <Link href="/services" className="hover:text-primary transition-colors">
              Services
            </Link>
            <Icon name="ChevronRightIcon" size={16} />
            <Link href="/services/menu" className="hover:text-primary transition-colors">
              {service.category}
            </Link>
            <Icon name="ChevronRightIcon" size={16} />
            <span className="text-foreground">{service.name}</span>
          </div>
        </div>

        {/* Hero Section */}
        <section className="max-w-[1200px] mx-auto px-6 mb-20 reveal">
          <div className="mb-6 flex flex-wrap items-center gap-3">
            <Badge variant="primary">{service.category}</Badge>
            <Badge variant="default">{service.typicalHours}</Badge>
          </div>

          <h1 className="text-h2 md:text-h1 text-white mb-6" style={{ fontSize: 'clamp(2rem, 4vw, 3rem)', lineHeight: '1.1' }}>{service.outcomeHeadline}</h1>
          <p className="text-h5 text-gray-100 mb-10 max-w-3xl">{service.subhead}</p>

          <div className="flex flex-wrap items-center gap-4 mb-10">
            <Link href="/book">
              <Button variant="primary" size="lg" className="border-glow">
                Book Consultation
              </Button>
            </Link>
            <Link href="/pricing">
              <Button variant="secondary" size="lg">
                View Pricing
              </Button>
            </Link>
            <Link href="/services/menu">
              <Button variant="ghost" size="lg">
                View Full Services Menu
              </Button>
            </Link>
          </div>

          {/* Section Jump Links */}
          <div className="flex flex-nowrap overflow-x-auto gap-3 pt-6 border-t border-border scrollbar-hide">
            {[
              { id: 'overview', label: 'Overview' },
              { id: 'who-this-is-for', label: 'Who This Is For' },
              { id: 'whats-included', label: "What's Included" },
              { id: 'time-capacity', label: 'Time & Capacity' },
              { id: 'modules', label: 'Modules' },
              { id: 'example-sprint', label: 'Example Sprint' },
              { id: 'proof', label: 'Proof' },
              { id: 'faqs', label: 'FAQs' },
            ].map((section) => (
              <a
                key={section.id}
                href={`#${section.id}`}
                className={`text-micro uppercase tracking-widest px-3 py-2 border transition-colors ${
                  activeSection === section.id
                    ? 'border-primary text-primary bg-primary/5' :'border-border text-gray-200 hover:border-primary hover:text-primary'
                }`}
              >
                {section.label}
              </a>
            ))}
          </div>
        </section>

        {/* Overview */}
        <section id="overview" className="max-w-[1200px] mx-auto px-6 mb-20 reveal">
          <div className="grid md:grid-cols-2 gap-12">
            <div>
              <h3 className="text-h3 text-white mb-6">Best For</h3>
              <ul className="space-y-3">
                {service.bestFor.map((item, idx) => (
                  <li key={idx} className="flex items-start gap-3">
                    <Icon name="CheckCircleIcon" size={20} className="text-success mt-1 flex-shrink-0" />
                    <span className="text-body text-gray-200">{item}</span>
                  </li>
                ))}
              </ul>

              <h3 className="text-h3 text-white mb-6 mt-10">Not Ideal For</h3>
              <ul className="space-y-3">
                {service.notIdealFor.map((item, idx) => (
                  <li key={idx} className="flex items-start gap-3">
                    <Icon name="XCircleIcon" size={20} className="text-error mt-1 flex-shrink-0" />
                    <span className="text-body text-gray-200">{item}</span>
                  </li>
                ))}
              </ul>
            </div>

            <div>
              <h3 className="text-h3 text-white mb-6">What You'll Get</h3>
              <ul className="space-y-3 mb-10">
                {service.whatYouGet.map((item, idx) => (
                  <li key={idx} className="flex items-start gap-3">
                    <Icon name="SparklesIcon" size={20} className="text-primary mt-1 flex-shrink-0" />
                    <span className="text-body text-gray-200">{item}</span>
                  </li>
                ))}
              </ul>

              <h3 className="text-h3 text-white mb-6">Prerequisites</h3>
              <ul className="space-y-3">
                {service.prerequisites.map((item, idx) => (
                  <li key={idx} className="flex items-start gap-3">
                    <Icon name="DocumentCheckIcon" size={20} className="text-gray-300 mt-1 flex-shrink-0" />
                    <span className="text-body text-gray-200">{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </section>

        {/* What's Included */}
        <section id="whats-included" className="max-w-[1200px] mx-auto px-6 mb-20 reveal">
          <SectionHeader eyebrow="Deliverables" title="What's Included" align="left" className="mb-10" />
          <div className="grid md:grid-cols-2 gap-4">
            {service.deliverables.map((item, idx) => (
              <div key={idx} className="flex items-start gap-3 p-4 border border-border hover:border-primary/50 transition-all group">
                <Icon name="CheckIcon" size={20} className="text-primary mt-1 flex-shrink-0" />
                <span className="text-body text-gray-200">{item}</span>
              </div>
            ))}
          </div>
          <p className="text-body-sm text-gray-300 mt-6 italic">
            Final scope is prioritized in sprints, this is the standard baseline.
          </p>
        </section>

        {/* Time & Capacity Explainer */}
        <section id="time-capacity" className="max-w-[1200px] mx-auto px-6 mb-20 reveal">
          <SectionHeader
            eyebrow="Capacity Planning"
            title="Time & Capacity Explainer"
            description="We don't bill hourly. We work in sprints with capacity buckets. Here's how this service fits."
            align="left"
            className="mb-10"
          />

          <div className="grid md:grid-cols-3 gap-6 mb-10">
            <div className="p-6 border border-border">
              <h4 className="text-h5 text-white mb-3">Execution Coverage</h4>
              <p className="text-body-sm text-gray-200 mb-4">40h/mo capacity bucket for ongoing execution</p>
              <div className="mb-3">
                <div className="flex items-center justify-between text-micro uppercase tracking-widest mb-2">
                  <span className="text-gray-200">This Service</span>
                  <span className="text-gray-200">{capacityPercentage}%</span>
                </div>
                <div className="h-2 bg-surface-1 overflow-hidden">
                  <div className="h-full bg-primary" style={{ width: `${capacityPercentage}%` }} />
                </div>
              </div>
              <p className="text-micro text-gray-300">Fits within standard monthly capacity</p>
            </div>

            <div className="p-6 border border-border">
              <h4 className="text-h5 text-white mb-3">Growth Partner</h4>
              <p className="text-body-sm text-gray-200 mb-4">Higher capacity + priority for heavier services</p>
              <Badge variant="primary" className="mb-3">
                Recommended for Heavy Months
              </Badge>
              <p className="text-micro text-gray-300">Best for multi-service execution or heavy project months</p>
            </div>

            <div className="p-6 border border-border">
              <h4 className="text-h5 text-white mb-3">AutoPilot</h4>
              <p className="text-body-sm text-gray-200 mb-4">DIY with templates and async support</p>
              <p className="text-micro text-gray-300">
                {service.capacityFit === 'light' ?'Good fit for lighter services with templates' :'Not ideal for this service - requires hands-on execution'}
              </p>
            </div>
          </div>

          {/* Visual Meter */}
          <div className="p-6 border border-border">
            <h4 className="text-h5 text-white mb-6">Typical Month Capacity</h4>
            <div className="space-y-4">
              <div>
                <div className="flex items-center justify-between mb-2">
                  <span className="text-body text-gray-200">Light Month</span>
                  <span className="text-body-sm text-gray-200">10-20h</span>
                </div>
                <div className="h-2 bg-surface-1 overflow-hidden">
                  <div className="h-full bg-success" style={{ width: '25%' }} />
                </div>
              </div>
              <div>
                <div className="flex items-center justify-between mb-2">
                  <span className="text-body text-gray-200">Standard Month</span>
                  <span className="text-body-sm text-gray-200">20-40h</span>
                </div>
                <div className="h-2 bg-surface-1 overflow-hidden">
                  <div className="h-full bg-primary" style={{ width: '50%' }} />
                </div>
              </div>
              <div>
                <div className="flex items-center justify-between mb-2">
                  <span className="text-body text-gray-200">Heavy Month</span>
                  <span className="text-body-sm text-gray-200">40-80h</span>
                </div>
                <div className="h-2 bg-surface-1 overflow-hidden">
                  <div className="h-full bg-warning" style={{ width: '75%' }} />
                </div>
              </div>
            </div>
            <p className="text-body-sm text-gray-200 mt-6">
              Heavy project months (photoshoots, launches, major campaigns) are planned in advance. We allocate capacity across sprints and
              coordinate with your timeline.
            </p>
          </div>
        </section>

        {/* Modules & Add-ons */}
        <section id="modules" className="max-w-[1200px] mx-auto px-6 mb-20 reveal">
          <SectionHeader
            eyebrow="Expand Your Sprint"
            title="Modules & Add-ons"
            description="Common add-ons that increase scope and value. These are scoped separately and planned in advance."
            align="left"
            className="mb-10"
          />
          <div className="grid md:grid-cols-3 gap-6">
            {service.modules.map((module, idx) => (
              <div key={idx} className="p-6 border border-border hover:border-primary/50 transition-all group">
                <h4 className="text-h5 text-white mb-3">{module.name}</h4>
                <p className="text-body-sm text-gray-200 mb-4">{module.description}</p>
                <Badge variant="default" className="mb-4">
                  {module.typicalHours}
                </Badge>
                <a href="#final-cta" className="text-label text-primary hover:underline flex items-center gap-2 group-hover:gap-3 transition-all">
                  Add to Sprint
                  <Icon name="ArrowRightIcon" size={16} />
                </a>
              </div>
            ))}
          </div>
        </section>

        {/* Example Sprint Month */}
        <section id="example-sprint" className="max-w-[1200px] mx-auto px-6 mb-20 reveal">
          <SectionHeader
            eyebrow="How We Work"
            title="Example Sprint Month"
            description="Here's what a typical 4-week sprint looks like for this service."
            align="left"
            className="mb-10"
          />
          <div className="grid md:grid-cols-4 gap-6">
            {service.exampleSprint.map((week, idx) => (
              <div key={idx} className="relative">
                <div className="absolute -left-3 top-0 w-12 h-12 rounded-full bg-primary/10 border-2 border-primary flex items-center justify-center">
                  <span className="text-label text-primary">W{week.week}</span>
                </div>
                <div className="pl-12">
                  <h4 className="text-h5 text-white mb-3">{week.title}</h4>
                  <p className="text-body-sm text-gray-200">{week.description}</p>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* Proof */}
        {service.testimonial && (
          <section id="proof" className="max-w-[1200px] mx-auto px-6 mb-20 reveal">
            <SectionHeader eyebrow="Client Success" title="Proof" align="left" className="mb-10" />
            <div className="p-8 border border-border bg-surface-1">
              <p className="text-h5 text-gray-100 mb-6 italic">"{service.testimonial.quote}"</p>
              <div className="flex items-center gap-4">
                <div>
                  <p className="text-body text-white font-semibold">{service.testimonial.name}</p>
                  <p className="text-body-sm text-gray-200">
                    {service.testimonial.title}, {service.testimonial.company}
                  </p>
                </div>
              </div>
            </div>
            <div className="mt-6">
              <Link href="/case-studies">
                <Button variant="secondary" size="md">
                  View Case Studies
                </Button>
              </Link>
            </div>
          </section>
        )}

        {/* FAQs */}
        <section id="faqs" className="max-w-[1200px] mx-auto px-6 mb-20 reveal">
          <SectionHeader eyebrow="Questions" title="Frequently Asked Questions" align="left" className="mb-10" />
          <Accordion
            items={service.faq.map((faq) => ({
              id: faq.id,
              title: faq.question,
              content: faq.answer,
            }))}
            allowMultiple
          />
        </section>

        {/* Related Services */}
        {relatedServices.length > 0 && (
          <section className="max-w-[1200px] mx-auto px-6 mb-20 reveal">
            <SectionHeader eyebrow="Explore More" title="Related Services" align="left" className="mb-10" />
            <div className="grid md:grid-cols-3 gap-6">
              {relatedServices.map((relatedService) => (
                <Link key={relatedService.slug} href={`/services/${relatedService.slug}`}>
                  <div className="p-6 border border-border hover:border-primary transition-all group h-full">
                    <div className="mb-4">
                      <Icon name={relatedService.icon} size={32} className="text-primary" />
                    </div>
                    <Badge variant="default" className="mb-3">
                      {relatedService.category}
                    </Badge>
                    <h4 className="text-h5 text-white mb-3 group-hover:text-primary transition-colors">{relatedService.name}</h4>
                    <p className="text-body-sm text-gray-200 mb-4">{relatedService.subhead}</p>
                    <div className="flex items-center gap-2 text-label text-primary group-hover:gap-3 transition-all">
                      Learn More
                      <Icon name="ArrowRightIcon" size={16} />
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </section>
        )}

        {/* Final CTA */}
        <section id="final-cta" className="max-w-[1200px] mx-auto px-6 reveal">
          <div className="p-12 border border-primary bg-primary/5 text-center">
            <h2 className="text-h2 text-white mb-4">Ready to ship real work?</h2>
            <p className="text-body text-gray-200 mb-8 max-w-2xl mx-auto">
              Book a consultation to discuss your project. US-based team, 24-hour response time, sprint-based delivery.
            </p>
            <div className="flex flex-wrap items-center justify-center gap-4">
              <Link href="/book">
                <Button variant="primary" size="lg" className="border-glow">
                  Book Consultation
                </Button>
              </Link>
              <Link href="/services/menu">
                <Button variant="secondary" size="lg">
                  View Full Services Menu
                </Button>
              </Link>
              <Link href="/pricing">
                <Button variant="ghost" size="lg">
                  View Pricing
                </Button>
              </Link>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </>
  );
}